import Phaser from 'phaser';
import { createParticles } from './particles';
import { playSpellSound } from './audio';
import { ensureThemeLoaded, getThemeData } from '../Utils';

export class SpellManager {
  constructor(scene) {
    this.scene = scene;
    this.spells = {};
    this.mages = [];
    this.decisionTimers = new Map();
    this.cooldowns = new Map(); // playerId:spellId -> endTime
    this.projectiles = [];
    this.particleEmitters = [];
  }

  async loadSpells() {
    try {
      // Ensure theme is loaded
      await ensureThemeLoaded();

      // Get spells data from theme
      const spellsData = getThemeData('spells', {});
      const spellSounds = getThemeData('spellSounds', []);

      // Load spell sound assets
      if (spellSounds) {
        spellSounds.forEach(sound => {
          if (!this.scene.cache.audio.exists(sound.key)) {
            this.scene.load.audio(sound.key, `assets/${sound.file}`);
          }
        });
        this.scene.load.start();
      }

      this.spells = spellsData;
      console.log('Spells loaded:', Object.keys(this.spells));
    } catch (e) {
      console.error('Failed to load spells:', e);
    }
  }

  assignMages(players) {
    // Clear existing mages
    this.mages = [];
    this.decisionTimers.clear();

    // Calculate number of mages based on player count (50:1 ratio)
    const totalPlayers = players.length;
    const mageCount = Math.max(1, Math.floor(totalPlayers / 50));

    // Randomly select players to be mages
    const shuffledPlayers = Phaser.Utils.Array.Shuffle([...players]);
    const selectedMages = []; //shuffledPlayers.slice(0, mageCount);

    selectedMages.forEach(player => {
      player.class = 'mage';
      player.mana = 100;
      player.maxMana = 100;
      player.manaRegen = 3; // 3 mana per second
      this.mages.push(player);
      this.decisionTimers.set(player.walletAddress, 0);

      //console.log(`Player ${player.name} assigned mage class`);
    });

    //console.log(`Assigned ${mageCount} mages out of ${totalPlayers} players`);
  }

  update(delta, players) {
    // Update mana regeneration for all mages
    this.mages.forEach(mage => {
      if (mage.isAlive && mage.mana < mage.maxMana) {
        mage.mana = Math.min(mage.maxMana, mage.mana + (mage.manaRegen * delta / 1000));
      }
    });

    // Update spell decision timers and casting
    this.mages.forEach(mage => {
      if (!mage.isAlive) return;

      // Update decision timer
      const timerKey = mage.walletAddress;
      const currentTimer = this.decisionTimers.get(timerKey) || 0;
      this.decisionTimers.set(timerKey, currentTimer + delta);

      // Make casting decision every 500ms
      if (currentTimer >= 500) {
        this.makeCastingDecision(mage, players);
        this.decisionTimers.set(timerKey, currentTimer - 500);
      }
    });

    // Update projectiles
    this.updateProjectiles(delta);

    // Clean up finished particle emitters
    this.particleEmitters = this.particleEmitters.filter(emitter => {
      if (emitter && (emitter.on === false || emitter.active === false)) {
        emitter.destroy();
        return false;
      }
      return true;
    });
  }

  makeCastingDecision(mage, players) {
    // Skip if in combat, low mana, or on cooldown
    if (mage.isBattling) return;

    // Check if any spell is available (has enough mana and off cooldown)
    const availableSpells = ['fireball', 'fireblast'].filter(spellId => {
      const spell = this.spells[spellId];
      if (!spell) return false;
      const hasMana = mage.mana >= spell.manaCost;
      const cooldownKey = `${mage.walletAddress}:${spellId}`;
      const isOffCooldown = (this.cooldowns.get(cooldownKey) || 0) < this.scene.time.now;
      return hasMana && isOffCooldown;
    });

    if (availableSpells.length === 0) return;

    // 10% chance to cast if conditions are met
    if (Math.random() < 0.1) {
      this.castSpell(mage, players);
    }
  }

  castSpell(mage, players) {
    // Choose between fireball and fireblast randomly
    const spellOptions = ['fireball', 'fireblast'];
    const chosenSpellId = Phaser.Utils.Array.GetRandom(spellOptions);
    const spell = this.spells[chosenSpellId];

    const targets = players.filter(p =>
      p !== mage &&
      p.isAlive &&
      Phaser.Math.Distance.Between(mage.sprite.x, mage.sprite.y, p.sprite.x, p.sprite.y) <= spell.range
    );

    if (targets.length === 0) return;

    // Sort by health (lowest first) and pick the weakest
    targets.sort((a, b) => a.health - b.health);
    const target = targets[0];

    //console.log(`${mage.name} casting ${spell.name} at ${target.name}`);

    // Deduct mana
    mage.mana -= spell.manaCost;

    // Set cooldown
    const cooldownKey = `${mage.walletAddress}:${chosenSpellId}`;
    this.cooldowns.set(cooldownKey, this.scene.time.now + spell.cooldown);

    // Start casting sequence
    this.startWarmup(mage, spell);
  }

  startWarmup(mage, spell) {
    // Create warmup particles
    const warmupResult = createParticles(this.scene, mage.sprite.x, mage.sprite.y, spell.particles.warmup);
    const warmupEmitter = warmupResult.main || warmupResult;
    this.particleEmitters.push(warmupEmitter);

    // Assign to world camera (ignore UI camera)
    if (this.scene.worldCamera) {
      this.scene.worldCamera.ignore(warmupEmitter);
    }

    // Handle smoke emitter if present
    if (warmupResult.smoke) {
      this.particleEmitters.push(warmupResult.smoke);
      if (this.scene.worldCamera) {
        this.scene.worldCamera.ignore(warmupResult.smoke);
      }
    }

    // Play warmup sound if available
    if (spell.sfx && spell.sfx.launch) {
      playSpellSound(this.scene, spell.sfx.launch);
    }

    // After warmup duration, launch the spell
    this.scene.time.delayedCall(spell.particles.warmup.duration || 800, () => {
      this.launchSpell(mage, spell);
      // Stop warmup particles
      warmupEmitter.stop();
    });
  }

  launchSpell(mage, spell) {
    if (spell.instant) {
      // Instant spells (like lightning)
      this.hitTarget(mage, { x: mage.sprite.x + 50, y: mage.sprite.y }, spell);
    } else {
      // Projectile spells (like fireball)
      this.createProjectile(mage, spell);
    }
  }

  createProjectile(mage, spell) {
    // Find target again (in case it moved)
    const players = this.scene.players || [];
    const targets = players.filter(p =>
      p.isAlive &&
      Phaser.Math.Distance.Between(mage.sprite.x, mage.sprite.y, p.sprite.x, p.sprite.y) <= spell.range
    );

    if (targets.length === 0) return;

    targets.sort((a, b) => a.health - b.health);
    const target = targets[0];

    const projectile = {
      x: mage.sprite.x,
      y: mage.sprite.y,
      targetX: target.sprite.x,
      targetY: target.sprite.y,
      speed: spell.speed,
      spell: spell,
      sprite: null,
      trailEmitter: null
    };

    // Create projectile sprite (invisible hitbox)
    projectile.sprite = this.scene.add.image(projectile.x, projectile.y, 'particle-yellow'); // Use yellow for fireball projectiles
    projectile.sprite.setVisible(false);
    projectile.sprite.setScale(0.1);

    // Create trail particles
    const trailResult = createParticles(this.scene, projectile.x, projectile.y, spell.particles.launch);
    projectile.trailEmitter = trailResult.main || trailResult;
    this.particleEmitters.push(projectile.trailEmitter);

    // Assign to world camera (ignore UI camera)
    if (this.scene.worldCamera) {
      this.scene.worldCamera.ignore(projectile.trailEmitter);
    }

    // Handle smoke trail if present
    if (trailResult.smoke) {
      projectile.smokeTrailEmitter = trailResult.smoke;
      this.particleEmitters.push(projectile.smokeTrailEmitter);
      if (this.scene.worldCamera) {
        this.scene.worldCamera.ignore(projectile.smokeTrailEmitter);
      }
    }

    this.projectiles.push(projectile);
  }

  updateProjectiles(delta) {
    this.projectiles.forEach((projectile, index) => {
      // Move toward target
      const dx = projectile.targetX - projectile.x;
      const dy = projectile.targetY - projectile.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 10) {
        // Hit target
        this.hitTarget(projectile, projectile, projectile.spell);
        projectile.sprite.destroy();
        // Stop trail emitters
        projectile.trailEmitter.stop();
        if (projectile.smokeTrailEmitter) {
          projectile.smokeTrailEmitter.stop();
        }
        this.projectiles.splice(index, 1);
        return;
      }

      // Move projectile
      const moveX = (dx / distance) * projectile.speed * (delta / 1000);
      const moveY = (dy / distance) * projectile.speed * (delta / 1000);

      projectile.x += moveX;
      projectile.y += moveY;
      projectile.sprite.x = projectile.x;
      projectile.sprite.y = projectile.y;

      // Update trail particles
      projectile.trailEmitter.setPosition(projectile.x, projectile.y);

      // Update smoke trail if present
      if (projectile.smokeTrailEmitter) {
        projectile.smokeTrailEmitter.setPosition(projectile.x, projectile.y);
      }
    });
  }

  hitTarget(source, position, spell) {
    // Create hit particles
    const hitResult = createParticles(this.scene, position.x, position.y, spell.particles.hit);
    const hitEmitter = hitResult.main || hitResult;
    this.particleEmitters.push(hitEmitter);

    // Assign to world camera (ignore UI camera)
    if (this.scene.worldCamera) {
      this.scene.worldCamera.ignore(hitEmitter);
    }

    // Handle smoke explosion if present
    if (hitResult.smoke) {
      this.particleEmitters.push(hitResult.smoke);
      if (this.scene.worldCamera) {
        this.scene.worldCamera.ignore(hitResult.smoke);
      }
    }

    // Play hit sound
    if (spell.sfx && spell.sfx.hit) {
      playSpellSound(this.scene, spell.sfx.hit);
    }

    // Find targets in radius and apply damage
    const players = this.scene.players || [];
    players.forEach(player => {
      if (!player.isAlive) return;

      const distance = Phaser.Math.Distance.Between(position.x, position.y, player.sprite.x, player.sprite.y);
      if (distance <= (spell.radius || 30)) {
        // Apply damage
        player.takeDamage(spell.damage);

        // Visual feedback
        this.scene.createAttackEffect(player, player);

        //console.log(`${spell.name} hit ${player.name} for ${spell.damage} damage`);
      }
    });
  }
}